<template>
  <div class="comm-history-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Communication history</h5>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="view-body text-center py-5">
      <i class="el-icon-loading" style="font-size: 24px;"></i>
    </div>

    <!-- Empty state -->
    <div v-else-if="rows.length === 0" class="view-body">
      <div class="empty-state">
        <i class="tim-icons icon-email-85 empty-icon"></i>
        <p class="empty-title">No emails yet</p>
        <p class="empty-sub">
          Emails sent to this customer will appear here.
        </p>
      </div>
    </div>

    <!-- History list -->
    <div v-else class="view-body">
      <p class="history-summary mb-2">
        Showing {{ rows.length }} email{{ rows.length === 1 ? '' : 's' }} for this transaction.
      </p>
      <div class="email-list">
        <div v-for="row in rows" :key="row.key" class="email-row">
          <!-- Type on the left, source (system or staff member) on the right. -->
          <div class="email-row__top">
            <span class="email-row__type">{{ formatEmailType(row.email_type) }}</span>
            <span class="email-row__sender">{{ row.sender_label }}</span>
          </div>

          <!-- Status chain full-width so badges (each carrying a date+time)
               have room to lay out without forcing aggressive wrap. Each
               badge+separator pair is grouped so the › can't orphan onto the
               next line when the chain wraps. -->
          <div class="status-chain">
            <span
              v-for="(event, i) in row.status_events"
              :key="event.status + '-' + i"
              class="status-chain__step"
            >
              <badge
                :type="statusBadgeType(event.status)"
                class="status-chain__badge"
              >
                <span class="status-chain__label">{{ formatStatus(event.status) }}</span>
                <span class="status-chain__time">{{ getBadgeDateTime(event.timestamp) }}</span>
              </badge>
              <span
                v-if="i < row.status_events.length - 1"
                class="status-chain__sep"
                aria-hidden="true"
              >›</span>
            </span>
          </div>

          <!-- Recipient -->
          <div class="email-row__recipient">
            <i class="tim-icons icon-email-85 mr-1"></i>
            {{ row.recipient_email || row.recipient_name || 'Unknown recipient' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { API_LOCATION } from '@/api-config';
import { shortDateTime } from '@/plugins/dates';
import { collapseEmailEvents } from '@/util/emailEvents';
import Badge from '@/components/Badge';

const STATUS_LABELS = {
  'Send': 'Sent',
  'Delivery': 'Delivered',
  'Open': 'Opened',
  'Click': 'Clicked',
  'Bounce': 'Bounced',
  'Complaint': 'Complaint',
  'Reject': 'Rejected',
  'Rendering Failure': 'Render failed',
  'DeliveryDelay': 'Delivery delayed',
  'Subscription': 'Unsubscribed'
};

// Map SES event types onto the app's default badge variants so each step in
// the progression chain reads distinctly using styles that already match
// every other badge in the app (no hand-rolled rgba). TransactionDetails
// uses `default` for both Send and Delivery; we split Delivery onto `info`
// here so the chain visualises the step-up.
const STATUS_BADGE_TYPE = {
  'Send': 'default',
  'Delivery': 'info',
  'Open': 'success',
  'Click': 'primary',
  'Subscription': 'warning',
  'DeliveryDelay': 'warning',
  'Bounce': 'danger',
  'Complaint': 'danger',
  'Reject': 'danger',
  'Rendering Failure': 'danger'
};

export default {
  name: 'CommunicationHistoryView',

  components: { Badge },

  props: {
    booking: { type: Object, default: () => ({}) },
    companyId: { type: [Number, String], default: null },
    companyGroupId: { type: [Number, String], default: null },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: '12h' }
  },

  emits: ['back'],

  data() {
    return {
      loading: false,
      rows: []
    };
  },

  mounted() {
    this.loadHistory();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    async loadHistory() {
      const transactionId = this.booking?.transaction_id;
      if (!transactionId) return;
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        // Lean group-scoped endpoint; transaction_id is the scope filter the
        // booking drawer always passes. Returns just the events array (no
        // bookings/payments/waivers/etc. payload). Default page size is 100;
        // bump to 500 (the lambda max) so a long-running booking with many
        // event rows fits in one round trip — pagination can be added later.
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/email-events?transaction_id=${transactionId}&count=500`;
        const response = await axios.get(url, config);
        const events = response.data?.email_events || [];
        this.rows = this.aggregate(collapseEmailEvents(events));
      } catch (error) {
        console.error('Failed to load communication history:', error);
        let message = 'Failed to load communication history';
        const raw = error?.response?.data;
        try {
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (parsed?.error) message = parsed.error;
        } catch (e) { /* keep default */ }
        this.$notify({
          type: 'danger',
          message,
          verticalAlign: 'top',
          horizontalAlign: 'right'
        });
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },

    aggregate(events) {
      // SES events for a single email arrive as separate rows (Send → Delivery
      // → Open → Click) with different timestamps. We want one card per email,
      // not one card per event. Strategy: chronologically replay the events.
      // A Send event always starts a new group. Non-Send events attach to the
      // most recent group for the same (email_type, recipient) so a Delivery
      // joins the Send above it. A second Send for the same pair later starts
      // a brand-new group (template was re-sent).
      //
      // Tiebreaker: when timestamps are identical (SES often emits Send and
      // Delivery in the same second), Send must sort first or it would land
      // after the Delivery and create a second group for the same email.
      const sorted = [...events]
        .filter(e => e && e.timestamp)
        .sort((a, b) => {
          const diff = new Date(a.timestamp) - new Date(b.timestamp);
          if (diff !== 0) return diff;
          if (a.event_type === 'Send' && b.event_type !== 'Send') return -1;
          if (b.event_type === 'Send' && a.event_type !== 'Send') return 1;
          return 0;
        });

      const groups = [];
      const lastGroupForKey = new Map();
      // Orphan groups are events that landed before any Send for their key.
      // If a Send for the same key arrives later we merge into it rather than
      // strand two half-records on screen (out-of-order webhook data).
      const pendingOrphans = new Map();

      for (const e of sorted) {
        const matchKey = `${e.email_type || 'unknown'}|${(e.customer_email || '').toLowerCase()}`;

        if (e.event_type === 'Send') {
          if (pendingOrphans.has(matchKey)) {
            const orphan = pendingOrphans.get(matchKey);
            orphan.events.unshift(e);
            lastGroupForKey.set(matchKey, orphan);
            pendingOrphans.delete(matchKey);
          } else {
            const grp = { events: [e] };
            groups.push(grp);
            lastGroupForKey.set(matchKey, grp);
          }
        } else if (lastGroupForKey.has(matchKey)) {
          lastGroupForKey.get(matchKey).events.push(e);
        } else {
          const grp = { events: [e] };
          groups.push(grp);
          pendingOrphans.set(matchKey, grp);
        }
      }

      const rows = groups.map(g => {
        const eventsByTime = [...g.events].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        const anchor = eventsByTime.find(e => e.event_type === 'Send') || eventsByTime[0];

        // Every event in temporal order, one badge each. Repeat Opens and
        // Clicks are kept (each with its own timestamp) so the chain matches
        // the raw event list on the customer and transaction pages.
        const statusEvents = eventsByTime
          .filter(e => e.event_type)
          .map(e => ({ status: e.event_type, timestamp: e.timestamp }));

        const recipientName = [anchor.customer_first_name, anchor.customer_last_name]
          .filter(Boolean)
          .join(' ')
          .trim();
        const senderName = [anchor.sender_first_name, anchor.sender_last_name]
          .filter(Boolean)
          .join(' ')
          .trim();
        const senderLabel = anchor.sender_email
          ? (senderName || anchor.sender_email)
          : 'System (automation)';

        return {
          key: `${anchor.timestamp}|${anchor.email_type}|${anchor.customer_email}`,
          email_type: anchor.email_type || 'unknown',
          sent_at: anchor.timestamp,
          recipient_email: anchor.customer_email,
          recipient_name: recipientName,
          sender_label: senderLabel,
          status_events: statusEvents
        };
      });

      return rows.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
    },

    formatEmailType(type) {
      if (!type) return 'Email';
      const spaced = String(type).replace(/_/g, ' ').trim();
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    },

    formatStatus(eventType) {
      return STATUS_LABELS[eventType] || eventType || 'Unknown';
    },

    statusBadgeType(eventType) {
      return STATUS_BADGE_TYPE[eventType] || 'default';
    },

    getBadgeDateTime(d) {
      return shortDateTime(d, this.dateFormat, this.timeFormat);
    }
  }
};
</script>

<style lang="scss" scoped>
.comm-history-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.view-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.back-btn {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.view-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.view-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
}

.email-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.email-row {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.history-summary {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
}

.email-row__top {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
}

.email-row__type {
  font-size: 13px;
  font-weight: 600;
  color: white;
}

.email-row__sender {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
  text-align: right;
}

.email-row__recipient {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: white;
  word-break: break-word;
}

.status-chain {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.status-chain__step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.status-chain__badge {
  ::v-deep .badge {
    font-size: 10px;
    padding: 3px 8px;
    margin: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
  }
}

.status-chain__label {
  font-weight: 600;
}

.status-chain__time {
  font-size: 9px;
  font-weight: 400;
  letter-spacing: 0;
  opacity: 0.75;
}

.status-chain__sep {
  color: rgba(255, 255, 255, 0.45);
  font-size: 14px;
  line-height: 1;
  user-select: none;
}

.empty-state {
  text-align: center;
  padding: 40px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.empty-icon {
  font-size: 32px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 12px;
}

.empty-title {
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.empty-sub {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  max-width: 280px;
}
</style>

<style lang="scss">
.white-content {
  .comm-history-view .view-header {
    border-bottom-color: #e3e3e3;
  }

  .comm-history-view .back-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;

    &:hover {
      background: #e9ecef;
      color: #344675;
    }
  }

  .comm-history-view .view-title {
    color: #344675;
  }

  .comm-history-view .email-row {
    background: #f5f6fa;
    border-color: #e3e3e3;
  }

  .comm-history-view .email-row__type {
    color: #344675;
  }

  .comm-history-view .email-row__sender {
    color: rgba(52, 70, 117, 0.65);
  }

  .comm-history-view .email-row__recipient {
    color: #344675;
  }

  .comm-history-view .history-summary {
    color: rgba(52, 70, 117, 0.7);
  }

  .comm-history-view .status-chain__sep {
    color: rgba(52, 70, 117, 0.4);
  }

  .comm-history-view .empty-icon {
    color: rgba(52, 70, 117, 0.3);
  }

  .comm-history-view .empty-title {
    color: #344675;
  }

  .comm-history-view .empty-sub {
    color: rgba(52, 70, 117, 0.7);
  }
}
</style>
